import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type PriceRequest } from "@/api";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatPrice } from "@/lib/utils";

/**
 * Защита от фейковых скидок (встреча 25.09.2026).
 *
 * Любое повышение промодерированной цены приходит сюда заявкой: карточка при
 * этом остаётся на витрине по прежней цене. Модератор видит три цены —
 * прошедшую модерацию, действующую сейчас и новую — и объяснение продавца.
 * Зачёркнутая «цена до скидки» выше промодерированной и есть попытка фейковой
 * скидки, поэтому она показывается отдельно.
 */
export function PriceRequestsPanel() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const query = useQuery({
    queryKey: ["price-requests", "pending", page],
    queryFn: () => api.products.priceRequests({
      status: "pending",
      page,
      pageSize
    })
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<PriceRequest | null>(null);
  const [reason, setReason] = useState("");
  const refresh = () => queryClient.invalidateQueries({
    queryKey: ["price-requests"]
  });
  const approve = async (request: PriceRequest) => {
    setBusy(request.id);
    setError(null);
    try {
      await api.products.approvePriceRequest(request.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };
  const reject = async () => {
    if (!rejecting || !reason.trim()) return;
    setBusy(rejecting.id);
    setError(null);
    try {
      await api.products.rejectPriceRequest(rejecting.id, reason.trim());
      setRejecting(null);
      setReason("");
      await refresh();
    } catch (e_0) {
      setError(e_0 instanceof Error ? e_0.message : String(e_0));
    } finally {
      setBusy(null);
    }
  };
  if (query.isError) return null;
  const rows = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;
  if (query.isSuccess && total === 0) return null;
  return <section data-tour="products-price-requests" className="space-y-3 rounded-md border bg-muted/20 p-3">
			<div className="flex flex-wrap items-center gap-2">
				<h2 className="text-sm font-semibold">
					{t("products.priceRequests.title", {
          defaultValue: "Повышение цены на модерации"
        })}
				</h2>
				<Badge variant="secondary">{total}</Badge>
				<p className="basis-full text-xs text-muted-foreground">
					{t("products.priceRequests.hint", {
          defaultValue: "До решения на витрине действует прежняя цена. Скидка считается только от цены, прошедшей модерацию."
        })}
				</p>
			</div>

			{error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">
					{error}
				</div>}

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>{t("products.priceRequests.col.product", {
              defaultValue: "Товар"
            })}</TableHead>
						<TableHead>{t("products.priceRequests.col.size", {
              defaultValue: "Размер"
            })}</TableHead>
						<TableHead className="text-right">
							{t("products.priceRequests.col.approved", {
              defaultValue: "Прошла модерацию"
            })}
						</TableHead>
						<TableHead className="text-right">
							{t("products.priceRequests.col.current", {
              defaultValue: "Действует сейчас"
            })}
						</TableHead>
						<TableHead className="text-right">
							{t("products.priceRequests.col.requested", {
              defaultValue: "Новая цена"
            })}
						</TableHead>
						<TableHead className="text-right">
							{t("products.priceRequests.col.growth", {
              defaultValue: "Рост"
            })}
						</TableHead>
						<TableHead>{t("products.priceRequests.col.reason", {
              defaultValue: "Объяснение продавца"
            })}</TableHead>
						<TableHead className="text-right">
							<span className="sr-only">{t("products.col.actions")}</span>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map(request_0 => <TableRow key={request_0.id}>
							<TableCell className="max-w-[260px] whitespace-normal">
								<Link to={`/products/${encodeURIComponent(request_0.productId)}`} className="line-clamp-2 font-medium text-primary hover:underline">
	{request_0.productName ?? request_0.productId}
</Link>
								<div className="text-xs text-muted-foreground">
									{[request_0.productArticle, request_0.shopName].filter(Boolean).join(" · ")}
								</div>
							</TableCell>
							<TableCell>{request_0.variantLabel || "—"}</TableCell>
							<TableCell className="text-right tabular-nums">
								{formatPrice(request_0.approvedPrice)}
							</TableCell>
							<TableCell className="text-right tabular-nums">
								<PriceWithOld price={request_0.currentPrice} oldPrice={request_0.currentOldPrice} />
							</TableCell>
							<TableCell className="text-right tabular-nums font-medium">
								<PriceWithOld price={request_0.requestedPrice} oldPrice={request_0.requestedOldPrice} />
								{request_0.requestedOldPrice != null && request_0.requestedOldPrice > request_0.approvedPrice && <div className="ml-auto max-w-[150px] whitespace-normal text-xs text-destructive">
											{t("products.priceRequests.fakeDiscount", {
                defaultValue: "«Цена до скидки» выше прошедшей модерацию"
              })}
										</div>}
							</TableCell>
							<TableCell className="text-right tabular-nums">
								+{request_0.increasePct.toLocaleString("ru-RU")} %
							</TableCell>
							<TableCell className="max-w-[200px] whitespace-normal text-sm">
								{request_0.sellerReason ?? <span className="text-muted-foreground">
										{t("products.priceRequests.noReason", {
                defaultValue: "Не указано"
              })}
									</span>}
							</TableCell>
							<TableCell className="text-right">
								<div className="flex justify-end gap-2">
									<Button size="sm" disabled={busy === request_0.id} onClick={() => void approve(request_0)}>
										{t("products.priceRequests.approve", {
                  defaultValue: "Одобрить"
                })}
									</Button>
									<Button size="sm" variant="outline" disabled={busy === request_0.id} onClick={() => {
                setReason("");
                setRejecting(request_0);
              }}>
										{t("products.priceRequests.reject", {
                  defaultValue: "Отклонить"
                })}
									</Button>
								</div>
							</TableCell>
						</TableRow>)}
				</TableBody>
			</Table>

			{total > pageSize && <div className="flex items-center justify-end gap-2 text-sm">
					<Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
						←
					</Button>
					<span>
						{page} / {Math.ceil(total / pageSize)}
					</span>
					<Button size="sm" variant="outline" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(page + 1)}>
						→
					</Button>
				</div>}

			<ConfirmDialog open={rejecting !== null} title={t("products.priceRequests.rejectTitle", {
      defaultValue: "Отклонить повышение цены"
    })} description={t("products.priceRequests.rejectHint", {
      defaultValue: "У товара останется прежняя цена. Причину увидит продавец."
    })} confirmLabel={t("products.priceRequests.reject", {
      defaultValue: "Отклонить"
    })} confirmVariant="destructive" busy={busy !== null} onClose={() => setRejecting(null)} onConfirm={() => void reject()}>
				<Input autoFocus value={reason} maxLength={500} placeholder={t("products.priceRequests.reasonPlaceholder", {
        defaultValue: "Например: рост цены без обоснования"
      })} onChange={e_1 => setReason(e_1.target.value)} />
			</ConfirmDialog>
		</section>;
}
function PriceWithOld(t0) {
  const $ = _c(10);
  const {
    price,
    oldPrice
  } = t0;
  let t1;
  if ($[0] !== price) {
    t1 = formatPrice(price);
    $[0] = price;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div>{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== oldPrice || $[5] !== price) {
    t3 = oldPrice != null && oldPrice > price && <div className="text-xs text-muted-foreground line-through">{formatPrice(oldPrice)}</div>;
    $[4] = oldPrice;
    $[5] = price;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div>{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * Правила цен площадки. Порога роста больше нет: любое повышение идёт на
 * модерацию. Настраивается только «скидка только на весь товар» — нужна ли
 * скидка на отдельный размер, решает заказчик; по умолчанию выключено.
 */
export function PriceModerationRule() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const rule = useQuery({
    queryKey: ["settings", "moderation"],
    queryFn: () => api.settings.getModeration()
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wholeOnly = rule.data?.discountWholeProductOnly === true;

  // Правило общее для всех магазинов — ошибку сохранения показываем, а не
  // глотаем: иначе галочка молча «не нажимается».
  const toggle = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.settings.updateModeration({
        discountWholeProductOnly: !wholeOnly
      });
      await queryClient.invalidateQueries({
        queryKey: ["settings", "moderation"]
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };
  if (rule.isError) return null;
  return <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 p-3 text-sm">
			<span>
				{t("products.moderationRule.any", {
        defaultValue: "Любое повышение цены уходит на модерацию, до решения действует прежняя цена. Снижение цены и скидки — сразу."
      })}
			</span>
			<label className="flex items-center gap-2">
				<input type="checkbox" className="size-4" checked={wholeOnly} disabled={saving || !rule.data} onChange={() => void toggle()} />
				{t("products.moderationRule.wholeProduct", {
        defaultValue: "Скидка только на весь товар (без скидки на отдельный размер)"
      })}
			</label>
			{error && <span className="basis-full text-destructive">{error}</span>}
		</div>;
}